import { whitelabelsStore } from '@/stores/whitelabels'
import { useMediaQuery } from '@vueuse/core'

const partnerKeys = ['auroraenergy', 'sonnendrive', 'carsales', 'erinliving']

// Check route if whitelabel partner,
// returns whitelabel partner's name (key).
export const useGetPartnerName = ({ overridePath, route } = {}) => {
	const getPath = () => {
		if (overridePath) return overridePath

		if (route) return route.path

		try {
			return useRoute().path
		} catch (e) {
			console.error(e)
			return ''
		}
	}

	const partnerName = computed(() => {
		const path = getPath()
		const match = path.match(/\/partners\/(?<name>[^/]+)/)
		return match?.groups?.name || null
	})

	return { partnerName }
}

// Whitelabel partners brand styling.
// Get styles from whitelable store.
export const useWhitelabelsStyles = () => {
	const whitelabels = whitelabelsStore()
	const { partnerName } = useGetPartnerName()

	const activePartnerKey = computed(() => {
		const key = partnerName.value
		return partnerKeys.includes(key) ? key : null
	})

	const getterMap = {
		auroraenergy: 'getAuroraEnergyStyles',
		sonnendrive: 'getSonnenDriveStyles',
		carsales: 'getCarsalesStyles',
		erinliving: 'getErinLivingStyles',
	}

	const whitelabel = computed(() => {
		const targetGetter = getterMap[activePartnerKey.value]

		// Dynamically access the store property using bracket notation
		return targetGetter ? whitelabels[targetGetter] : {}
	})

	return { whitelabel }
}

// Dynamic auth paths for whitelabel partners.
// If routes are whitelabel partners, get respective partners auth paths.
// Defaults to carbar's auth paths.
export const useAuthPaths = (path) => {
	const { partnerName } = useGetPartnerName(path)
	const { whitelabel } = useWhitelabelsStyles(partnerName.value, path)

	const signInUrl = computed(() => {
		const isPartner = !!partnerName.value
		const partnerUrl = whitelabel.value?.links?.signInUrl

		return isPartner && partnerUrl ? partnerUrl : '/secure/login'
	})

	const signUpUrl = computed(() => {
		const isPartner = !!partnerName.value
		const partnerUrl = whitelabel.value?.links?.signUpUrl

		return isPartner && partnerUrl ? partnerUrl : '/sign-up'
	})

	const accountUrl = computed(() => {
		const isPartner = !!partnerName.value
		const partnerUrl = whitelabel.value?.links?.partnerUrl

		return isPartner && partnerUrl ? partnerUrl : '/dashboard'
	})

	const verifyOtpUrl = computed(() => {
		const isPartner = !!partnerName.value
		const partnerUrl = whitelabel.value?.links?.verifyOtpUrl

		return isPartner && partnerUrl ? partnerUrl : '/verify-otp'
	})

	const forgotPasswordUrl = computed(() => {
		const isPartner = !!partnerName.value
		const partnerUrl = whitelabel.value?.links?.forgotPasswordUrl

		return isPartner && partnerUrl ? partnerUrl : '/forgot-password'
	})

	const resetPasswordUrl = computed(() => {
		const isPartner = !!partnerName.value
		const partnerUrl = whitelabel.value?.links?.resetPasswordUrl

		return isPartner && partnerUrl ? partnerUrl : '/reset-password'
	})

	const navigateToLogin = () => {
		return navigateTo(signInUrl.value, { external: true })
	}

	const navigateToSignUp = () => {
		return navigateTo(signUpUrl.value, { external: true })
	}

	const navigateToVerifyOtp = () => {
		return navigateTo(verifyOtpUrl.value, { external: true })
	}

	const navigateToAccount = () => {
		return navigateTo(accountUrl.value, { external: true })
	}

	const isAuthPage = computed(() => {
		return [
			signInUrl.value,
			signUpUrl.value,
			verifyOtpUrl.value,
			forgotPasswordUrl.value,
			resetPasswordUrl.value,
		].includes(path) // using the 'path' argument passed to the composable
	})

	return {
		signInUrl,
		signUpUrl,
		accountUrl,
		verifyOtpUrl,
		forgotPasswordUrl,
		resetPasswordUrl,
		navigateToLogin,
		navigateToSignUp,
		navigateToAccount,
		navigateToVerifyOtp,
		isAuthPage,
		isPartnerPath: computed(() => !!partnerName.value),
	}
}

export const useResponsiveBgImage = (desktopUrl, mobileUrl) => {
	const bgImage = ref(`url('${mobileUrl}')`) // default mobile fallback

	onMounted(() => {
		const isDesktop = useMediaQuery('(min-width: 1024px)')

		bgImage.value = isDesktop.value
			? `url('${desktopUrl}')`
			: `url('${mobileUrl}')`

		watch(isDesktop, (val) => {
			bgImage.value = val ? `url('${desktopUrl}')` : `url('${mobileUrl}')`
		})
	})

	return { bgImage }
}
